import { FunctionComponent, useMemo } from "react";
import { FieldProps, FieldValueMeta } from "@animech/axiom-ui-builder";
import { ColorPicker, ColorPickerColor } from "./color-picker";
import { Root } from "@definitions";
import { useUiData } from "src/ui/data";
import { Property } from "src/common";

export type ColorPickerFieldProps = FieldProps<Root> & {
	visible?: boolean;
	position?: { x: number; y: number };
	onMove?: () => void;
	onDelete?: () => void;
};

export const ColorPickerField: FunctionComponent<ColorPickerFieldProps> = ({
	fieldValues = [],
	value,
	onValueChange,
	visible,
	position,
	onMove,
	fieldValuesMeta = [],
	property,
}) => {
	const { visualization, getInStockDomainFromColorProperty } = useUiData();
	const { deleteSelected, canDeleteSelected } = visualization;

	const inStockColors = useMemo(() => {
		return getInStockDomainFromColorProperty(property as Property);
	}, [property, getInStockDomainFromColorProperty]);

	const currentColorValue = value?.toString() || "";

	const [fieldValuesInStock, fieldValuesMetaInStock] = fieldValues.reduce(
		(acc: [unknown[], FieldValueMeta[]], fv, idx) => {
			if (inStockColors.includes(fv) || currentColorValue === fv) {
				acc[0].push(fv);
				acc[1].push(fieldValuesMeta[idx]);
			}
			return acc;
		},
		[[], []] as [unknown[], FieldValueMeta[]],
	);

	const colors: ColorPickerColor[] = useMemo(() => {
		return fieldValuesInStock.map((fieldValue, index) => {
			const { label, image } = fieldValuesMetaInStock[index] || {};

			return {
				label: label as string,
				image,
				value: fieldValue as string,
				outOfStock: !inStockColors.includes(fieldValue),
			};
		});
	}, [fieldValuesInStock, fieldValuesMetaInStock, inStockColors]);

	const selectedColor =
		colors.find((color) => color.value === currentColorValue) || colors[0];
	const canDelete = canDeleteSelected();

	const handleColorSelect = (color: ColorPickerColor) => {
		const valueIndex = fieldValuesInStock.findIndex(
			(value) => value?.toString() === color.value,
		);
		if (valueIndex >= 0 && onValueChange) {
			onValueChange(fieldValuesInStock[valueIndex], valueIndex);
		}
	};

	const handleMove = () => {
		onMove?.();
	};

	const handleDelete = () => {
		deleteSelected();
	};

	return (
		<ColorPicker
			visible={visible}
			position={position}
			colors={colors}
			selectedColor={selectedColor}
			canDelete={canDelete}
			onColorSelect={handleColorSelect}
			onMove={handleMove}
			onDelete={handleDelete}
		/>
	);
};
